import { softDepName } from "@skaile/workspaces/asset-manager";
import pc from "picocolors";

/**
 * Terminal styling helpers used across all CLI commands.
 *
 * @docLink cli/dev-guide#helpers
 */
export const S = {
  brand: (s: string) => pc.cyan(pc.bold(s)),
  cmd: (s: string) => pc.cyan(s),
  heading: (s: string) => pc.bold(s),
  bold: (s: string) => pc.bold(s),
  dim: (s: string) => pc.dim(s),
  ok: (s: string) => pc.green(s),
  warn: (s: string) => pc.yellow(s),
  err: (s: string) => pc.red(s),
  label: (s: string) => pc.dim(s),
  rule: (w = 40) => pc.dim("─".repeat(w)),
};

const KIND_COLORS: Record<string, (s: string) => string> = {
  skill: pc.blue,
  agent: pc.magenta,
  prompt: pc.yellow,
  flow: pc.green,
  bundle: pc.cyan,
  contract: pc.red,
  connector: pc.green,
  mount: pc.green,
  knowledge: pc.cyan,
  persona: pc.cyan,
  ruleset: pc.cyan,
  "mcp-server": pc.magenta,
};

/**
 * Fit a plain (ANSI-free) string into exactly `width` columns: truncate with a
 * trailing `…` when too long, pad with spaces when too short. Keeps fixed-width
 * columns from overflowing the row when a value (e.g. an asset name) is wider
 * than its budget.
 */
export function fitWidth(s: string, width: number): string {
  if (width <= 0) return "";
  if (s.length > width) return width === 1 ? "…" : `${s.slice(0, width - 1)}…`;
  return s.padEnd(width);
}

/** Color-code an asset kind string. */
export function kindColor(kind: string): string {
  const colorFn = KIND_COLORS[kind] ?? pc.dim;
  return colorFn(kind);
}

/**
 * Widest kind label (`mcp-server`); the default kind-column width fits it so
 * long kinds don't shove the rest of the row out of alignment.
 */
export const KIND_COL_WIDTH = 10;

/**
 * Color-code an asset kind string fit to exactly `width` columns — pads short
 * kinds, truncates over-long ones (via {@link fitWidth}) so the column can never
 * overflow and break row alignment.
 */
export function kindColorPad(kind: string, width = KIND_COL_WIDTH): string {
  const colorFn = KIND_COLORS[kind] ?? pc.dim;
  return colorFn(fitWidth(kind, width));
}

/** Color-code a "kind:name" reference string. */
export function colorRef(ref: string): string {
  const colonIdx = ref.indexOf(":");
  if (colonIdx === -1) return ref;
  const kind = ref.slice(0, colonIdx);
  const rest = ref.slice(colonIdx);
  return `${kindColor(kind)}${rest}`;
}

/**
 * Print a success line prefixed with a green check mark.
 *
 * @param msg - Message to display.
 * @docLink cli/dev-guide#helpers
 */
export function logOk(msg: string) {
  console.log(`  ${pc.green("✓")} ${msg}`);
}
/**
 * Print an error line prefixed with a red cross to stderr.
 *
 * @param msg - Message to display.
 * @docLink cli/dev-guide#helpers
 */
export function logErr(msg: string) {
  console.error(`  ${pc.red("✗")} ${msg}`);
}
/**
 * Print a warning line prefixed with a yellow exclamation mark.
 *
 * @param msg - Message to display.
 * @docLink cli/dev-guide#helpers
 */
export function logWarn(msg: string) {
  console.log(`  ${pc.yellow("!")} ${msg}`);
}
/**
 * Print an info line prefixed with a dim dot.
 *
 * @param msg - Message to display.
 * @docLink cli/dev-guide#helpers
 */
export function logInfo(msg: string) {
  console.log(`  ${pc.dim("·")} ${msg}`);
}

/**
 * Bare asset name of a canonical soft-dep target ref (`@pub/name@ver` → `name`),
 * for compact rendering in `info` / `tree` / `why`. Delegates to the asset-manager
 * parser (the soft-dep read surface) so the two never diverge.
 */
export function bareSoftDepName(ref: string): string {
  return softDepName(ref);
}

/**
 * The report-only soft-dep hint line shared by the `add` no-TTY branch and the
 * `install` surface, so the two user-facing wordings can't drift. Appends a
 * runnable `skaile add` suffix only when the target has a parseable ref.
 */
export function formatSoftDepHint(edge: { from: string; to: string; toRef?: string }): string {
  const suffix = edge.toRef ? ` — run \`skaile add ${edge.toRef}\` to include it.` : "";
  return `${bareSoftDepName(edge.from)} references ${bareSoftDepName(edge.to)} (soft dependency)${suffix}`;
}

/**
 * Build the "Soft dependencies (inferred)" section for `skaile info`. Returns one
 * header line plus one line per soft-dep target, each marked `(soft)` to read
 * differently from a typed `requires`. Empty array when there are no soft deps,
 * so the caller prints nothing.
 */
export function formatSoftDepSection(targets: string[]): string[] {
  if (targets.length === 0) return [];
  const out = [`  ${S.label("Soft dependencies (inferred)")}:`];
  for (const t of targets) out.push(`    ${S.dim("·")}  ${t} ${S.dim("(soft)")}`);
  return out;
}

/**
 * Footer line previewing the soft deps that *adding* the highlighted asset would
 * reference (manage TUI). "references" + "not auto-added" makes clear the pull is
 * report-only. Empty string when there are no targets, so the caller clears.
 */
export function formatSoftDepAddPreview(name: string, targets: string[]): string {
  if (targets.length === 0) return "";
  return S.dim(`${name} references ${targets.join(", ")} (soft dep, not auto-added)`);
}

/**
 * Footer line previewing the soft deps that *removing* the highlighted asset
 * would orphan (manage TUI) — symmetric with {@link formatSoftDepAddPreview}.
 * Empty string when nothing would be orphaned, so the caller clears.
 */
export function formatSoftDepRemovePreview(name: string, targets: string[]): string {
  if (targets.length === 0) return "";
  return S.dim(`removing ${name} would orphan ${targets.join(", ")} (soft dep)`);
}

/** Format a Date as a human-readable relative time string (e.g. "5m ago"). */
export function formatRelativeTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
